import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from './services/supabase';
import {
  ChevronLeft, ChevronRight, Calendar, Clock,
  MapPin, Package, Truck, CheckCircle, Loader2, Utensils, FileText, ArrowRight
} from 'lucide-react';

const ScheduleStyles = () => (
  <style>{`
    .cs-page {
      max-width: 960px;
      margin: 0 auto;
      padding: 40px 20px;
      min-height: calc(100vh - 140px);
    }

    /* Date nav */
    .cs-date-nav {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      padding: 14px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      position: sticky;
      top: 80px;
      z-index: 40;
    }
    .cs-date-btn {
      width: 36px; height: 36px; border-radius: 50%;
      border: 1px solid #e5e7eb; background: white;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; color: #64748b; transition: all 0.2s;
    }
    .cs-date-btn:hover { border-color: #2F7D5E; color: #2F7D5E; }
    .cs-date-center { text-align: center; }
    .cs-date-main { font-size: 1.1rem; font-weight: 400; color: #1e293b; }
    .cs-date-sub { font-size: 0.85rem; color: #94a3b8; margin-top: 2px; }

    /* Card list */
    .cs-list { display: flex; flex-direction: column; gap: 14px; }

    /* Delivery card */
    .cs-card {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      overflow: hidden;
      transition: box-shadow 0.2s;
    }
    .cs-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.05); }

    .cs-card-main {
      display: flex; align-items: stretch; padding: 18px 20px; gap: 16px;
    }

    /* Type icon */
    .cs-type-icon {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; gap: 6px; flex-shrink: 0;
    }
    .cs-type-circle {
      width: 48px; height: 48px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
    }
    .cs-type-circle.lunch { background: #fef3c7; color: #92400e; }
    .cs-type-circle.dinner { background: #dbeafe; color: #1e40af; }
    .cs-type-label {
      font-size: 0.75rem; font-weight: 400;
    }
    .cs-type-label.lunch { color: #92400e; }
    .cs-type-label.dinner { color: #1e40af; }

    /* Card body */
    .cs-card-body { flex: 1; min-width: 0; }

    .cs-card-top {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 10px;
    }
    .cs-order-id {
      font-family: 'SF Mono', 'Fira Code', monospace;
      font-size: 0.85rem; font-weight: 400; color: #64748b;
    }
    .cs-status {
      font-size: 0.8rem; font-weight: 400;
      display: flex; align-items: center; gap: 4px;
    }
    .cs-status-pending { color: #94a3b8; }
    .cs-status-delivering { color: #2563eb; }
    .cs-status-completed { color: #2F7D5E; }

    /* Meal row */
    .cs-meal-row { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
    .cs-meal-img {
      width: 50px; height: 50px; border-radius: 10px;
      object-fit: cover; flex-shrink: 0; border: 1px solid #e5e7eb;
    }
    .cs-meal-img-placeholder {
      width: 50px; height: 50px; border-radius: 10px;
      background: white; border: 1px dashed #e5e7eb;
      display: flex; align-items: center; justify-content: center;
      color: #cbd5e1; flex-shrink: 0;
    }
    .cs-meal-info { flex: 1; min-width: 0; }
    .cs-meal-name {
      font-size: 1rem; font-weight: 400; color: #1e293b; margin-bottom: 2px;
    }
    .cs-meal-mods {
      font-size: 0.8rem; color: #64748b;
    }

    /* Info grid */
    .cs-info-grid {
      display: flex; flex-wrap: wrap; gap: 12px;
      padding-top: 10px; border-top: 1px solid #f1f5f9;
    }
    .cs-info-item {
      display: flex; align-items: center; gap: 6px;
      font-size: 0.85rem; color: #64748b;
    }
    .cs-info-item svg { color: #94a3b8; flex-shrink: 0; }
    .cs-note-item {
      width: 100%;
      display: flex; align-items: flex-start; gap: 6px;
      font-size: 0.85rem; color: #2F7D5E; font-weight: 400;
    }
    .cs-note-item svg { color: #2F7D5E; flex-shrink: 0; margin-top: 2px; }

    /* Empty / Loading */
    /* Banner */
    .cs-banner {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      padding: 16px 20px;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
    .cs-banner-text {
      flex: 1;
    }
    .cs-banner-title {
      font-size: 0.95rem; font-weight: 400; color: #1e293b; margin-bottom: 4px;
    }
    .cs-banner-desc {
      font-size: 0.82rem; color: #64748b; line-height: 1.4;
    }
    .cs-banner-btn {
      display: flex; align-items: center; gap: 6px;
      padding: 10px 20px;
      background: #2F7D5E; color: white;
      border: none; border-radius: clamp(12px, 3vw, 20px);
      font-size: 0.85rem; font-weight: 400;
      cursor: pointer; white-space: nowrap;
      transition: background 0.2s;
    }
    .cs-banner-btn:hover { background: #245F48; }

    .cs-empty {
      text-align: center; padding: 80px 20px; color: #94a3b8;
      background: white; border: 1px solid #e5e7eb; border-radius: 4px;
    }
    .cs-empty-icon { margin-bottom: 12px; color: #cbd5e1; }
    .cs-empty h3 { font-weight: 400; color: #64748b; font-size: 1rem; margin-bottom: 4px; }
    .cs-empty p { font-size: 0.85rem; }
    .cs-empty-btn {
      display: inline-flex; align-items: center; gap: 6px;
      margin-top: 20px; padding: 12px 28px;
      background: #2F7D5E; color: white;
      border: none; border-radius: 4px;
      font-size: 0.9rem; font-weight: 400;
      cursor: pointer; transition: background 0.2s;
    }
    .cs-empty-btn:hover { background: #245F48; }

    .cs-loading {
      display: flex; justify-content: center; align-items: center;
      height: 300px; color: #94a3b8; gap: 10px; font-size: 0.9rem;
    }
    .cs-spinner { animation: cs-spin 1s linear infinite; }
    @keyframes cs-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    @media (max-width: 600px) {
      .cs-page { padding: 24px 16px; }
      .cs-card-main { flex-direction: column; gap: 12px; }
      .cs-type-icon { flex-direction: row; gap: 8px; }
      .cs-info-grid { flex-direction: column; gap: 8px; }
    }
  `}</style>
);

const ClientSchedulePage = ({ currentUser }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('schedule');
  const locale = i18n.language === 'en' ? 'en-US' : 'zh-TW';
  const isEn = i18n.language === 'en';
  const getItemName = (item) => isEn ? (item.name_en || item.name) : (item.name_zh || item.name);
  const getModName = (m) => isEn ? (m.name_en || m.name) : (m.name_zh || m.alternate_name || m.name);

  const [currentDate, setCurrentDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const [allDeliveries, setAllDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUserOrders = useCallback(async () => {
    try {
      const { data: orders, error } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const parsedDeliveries = [];

      orders.forEach(order => {
        const itemsData = order.items_data || {};
        const trackingData = order.delivery_tracking || {};
        const deliveryInfo = order.delivery_info || {};
        const scheduleData = order.delivery_schedule || {};

        Object.keys(itemsData).forEach(dayKey => {
          const dayItem = itemsData[dayKey];
          const dateStr = dayItem.date_info?.iso_date;
          if (!dateStr) return;

          const generateKey = (type) => `${order.id}_${dayKey}_${type}`;

          if (dayItem.lunch) {
            const trackingKey = generateKey('lunch');
            parsedDeliveries.push({
              uniqueId: trackingKey,
              orderId: order.order_number || order.id.slice(0, 8),
              date: dateStr,
              type: 'lunch',
              meal: dayItem.lunch,
              status: trackingData[trackingKey]?.status || 'pending',
              timeSlot: scheduleData[`${dayKey}_lunch`] || '11:00-12:00',
              address: deliveryInfo.address,
              notes: deliveryInfo.delivery_notes
            });
          }

          if (dayItem.dinner) {
            const trackingKey = generateKey('dinner');
            parsedDeliveries.push({
              uniqueId: trackingKey,
              orderId: order.order_number || order.id.slice(0, 8),
              date: dateStr,
              type: 'dinner',
              meal: dayItem.dinner,
              status: trackingData[trackingKey]?.status || 'pending',
              timeSlot: scheduleData[`${dayKey}_dinner`] || '17:00-18:00',
              address: deliveryInfo.address,
              notes: deliveryInfo.delivery_notes
            });
          }
        });
      });

      setAllDeliveries(parsedDeliveries);
    } catch (err) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    fetchUserOrders();

    const channel = supabase
      .channel('public:orders')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${currentUser.id}`
        },
        () => fetchUserOrders()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentUser, fetchUserOrders]);

  const dailyDeliveries = useMemo(() => {
    const targetYear = currentDate.getFullYear();
    const targetMonth = currentDate.getMonth() + 1;
    const targetDay = currentDate.getDate();

    return allDeliveries
      .filter(item => {
        const datePart = item.date.split('T')[0];
        const [y, m, d] = datePart.split('-').map(Number);
        return y === targetYear && m === targetMonth && d === targetDay;
      })
      .sort((a, b) => {
        if (a.type === 'lunch' && b.type === 'dinner') return -1;
        if (a.type === 'dinner' && b.type === 'lunch') return 1;
        return 0;
      });
  }, [currentDate, allDeliveries]);

  const changeDate = (days) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    newDate.setHours(0, 0, 0, 0);
    setCurrentDate(newDate);
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'completed': return { text: t('status.completed'), icon: <CheckCircle size={14} />, cls: 'cs-status-completed' };
      case 'delivering': return { text: t('status.delivering'), icon: <Truck size={14} />, cls: 'cs-status-delivering' };
      default: return { text: t('status.pending'), icon: <Package size={14} />, cls: 'cs-status-pending' };
    }
  };

  return (
    <>
      <style>{`
        .cs-coming-soon {
          max-width: 960px;
          margin: 0 auto;
          padding: 40px 20px;
          min-height: calc(100vh - 140px);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cs-coming-inner {
          text-align: center;
          max-width: 420px;
        }
        .cs-coming-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0faf5;
          border-radius: 50%;
          color: #2F7D5E;
        }
        .cs-coming-title {
          font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
          font-size: 1.75rem;
          font-weight: 600;
          color: #111;
          margin-bottom: 12px;
        }
        .cs-coming-desc {
          font-size: 1.05rem;
          color: #666;
          line-height: 1.7;
          margin-bottom: 32px;
        }
        .cs-coming-divider {
          width: 48px;
          height: 3px;
          background: #2F7D5E;
          border-radius: 2px;
          margin: 0 auto 32px;
        }
        .cs-coming-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 28px;
          background: #2F7D5E;
          color: white;
          border: none;
          border-radius: 4px;
          font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
          font-size: 1rem;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .cs-coming-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(47,125,94,0.3);
        }
      `}</style>
      <div className="cs-coming-soon">
        <div className="cs-coming-inner">
          <div className="cs-coming-icon">
            <Calendar size={36} />
          </div>
          <h2 className="cs-coming-title">即將上線</h2>
          <p className="cs-coming-desc">
            日程功能即將上線，<br />敬請期待！
          </p>
          <div className="cs-coming-divider" />
          <button className="cs-coming-btn" onClick={() => navigate('/booking/single')}>
            先去點餐 <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </>
  );
};

export default ClientSchedulePage;
